import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { Db2LinkedService } from 'app/model/linkedservice/db2.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV, akvToResource, akvToModel, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { isString } from 'app/shared/utils/string.util';
import { Constants } from 'app/shared/shared-constants';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AdapterContext } from 'shell-models-lib';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Db2LinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                database: '*',
                authenticationType: '*',
                username: '*',
                packageCollection: '*',
                certificateCommonName: '*',
                password: 'secret',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: Db2LinkedService) => {
                    // when AKV is used for connection string, it is already well handled. So we don't need handle it.
                    // won't cover fully by json for now
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
                        if (ls.password instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class Db2LinkedServiceAdapter extends LinkedServiceAdapter<Db2LinkedService> {
    protected supportFirstClassParameterExperience = true;
    protected modelConstructor(): new () => Db2LinkedService {
        return Db2LinkedService;
    }

    protected model(): IModel {
        return Db2LinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: Db2LinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.Db2LinkedService;
        properties.type = Metamodel.modelFor(Db2LinkedService).name;

        if (linkedService.enableAKV) {
            properties.typeProperties = {
                connectionString: akvToResource(linkedService.referedAKV, this.context)
            };
            return linkedServiceResource;
        }

        // it's new create or existing resource with connectionString, check IR version
        if (!linkedService.isOldMashupResource) {
            linkedService.isMashupResource = false;

            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService();
                builder.add('server', connectionStringPartToResource(linkedService, Db2LinkedService.SERVER_NAME));
                builder.add('database', connectionStringPartToResource(linkedService, Db2LinkedService.DATABASE_NAME));
                builder.add('username', connectionStringPartToResource(linkedService, Db2LinkedService.USER_NAME));
                if (linkedService.packageCollection || Db2LinkedService.PACKAGE_COLLECTION.templateValue(linkedService)) {
                    builder.add('packageCollection', connectionStringPartToResource(linkedService, Db2LinkedService.PACKAGE_COLLECTION));
                }
                if (linkedService.certificateCommonName || Db2LinkedService.CERTIFICATE_COMMON_NAME.templateValue(linkedService)) {
                    builder.add('certificateCommonName', connectionStringPartToResource(linkedService, Db2LinkedService.CERTIFICATE_COMMON_NAME));
                }
                if (!(linkedService.password instanceof AzureKeyVault) && linkedService.password) {
                    builder.add('Password', connectionStringPartToResource(linkedService, Db2LinkedService.PASSWORD));
                }
                if (linkedService.authenticationType) {
                    builder.add('authenticationType', linkedService.authenticationType);
                }
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        builder.add(pair.key, pair.value || '');
                    });
                }
                const connectionString = this._connectionStringToResource(builder.connectionString);
                properties.typeProperties = {
                    connectionString,
                    password: (linkedService.password instanceof AzureKeyVault) ? akvToResource(linkedService.password, this.context) : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAKV, this.context)
                };
            }
            return linkedServiceResource;
        }

        properties.typeProperties = {
            server: linkedService.server,
            database: linkedService.database,
            authenticationType: linkedService.authenticationType,
            username: linkedService.username,
            packageCollection: linkedService.packageCollection,
            certificateCommonName: linkedService.certificateCommonName,
            password: passwordOrAKVtoSecretBase(linkedService.password, this.context)
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: Db2LinkedService): Promise<Db2LinkedService> {
        const properties = linkedServiceResource.properties as Model.Db2LinkedService;
        const typeProperties: Model.Db2LinkedServiceTypeProperties = properties.typeProperties;
        const connectionString = typeProperties.connectionString;

        if (connectionString == null) {
            linkedService.isOldMashupResource = true;
            linkedService.isMashupResource = true;
            linkedService.server = typeProperties.server;
            linkedService.database = typeProperties.database;
            linkedService.authenticationType = typeProperties.authenticationType;
            linkedService.username = typeProperties.username;
            linkedService.packageCollection = typeProperties.packageCollection;
            linkedService.certificateCommonName = typeProperties.certificateCommonName;
            linkedService.password = secretBaseToPasswordOrAKV(properties.typeProperties.password, typeProperties.authenticationType === 'Basic', this.context);
        } else {
            linkedService.isOldMashupResource = false;
            linkedService.isMashupResource = false;
            if (isString(connectionString)) {
                const builder = new DbConnectionStringBuilderService();
                builder.parse(typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, Db2LinkedService.SERVER_NAME, builder.getValue('server', false) || '');
                connectionStringPartToModel(linkedService, Db2LinkedService.DATABASE_NAME, builder.getValue('database', false) || '');
                connectionStringPartToModel(linkedService, Db2LinkedService.USER_NAME, builder.getValue('username', false) || '');
                connectionStringPartToModel(linkedService, Db2LinkedService.PACKAGE_COLLECTION, builder.getValue('packageCollection', false) || '');
                connectionStringPartToModel(linkedService, Db2LinkedService.CERTIFICATE_COMMON_NAME, builder.getValue('certificateCommonName', false) || '');
                if (typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, Db2LinkedService.PASSWORD, typeProperties.password, this.context);
                } else {
                    linkedService.password = Constants.maskedCredential;
                }
                linkedService.authenticationType = builder.getValue('authenticationType', false) || '';
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['server', 'database', 'authenticationType', 'username', 'packageCollection', 'certificateCommonName', 'Password']);
            } else if (SecureStringReplacer.isSecureStringObject(connectionString)) {
                linkedService.server = Constants.maskedCredential;
                linkedService.database = Constants.maskedCredential;
                linkedService.username = Constants.maskedCredential;
                linkedService.packageCollection = Constants.maskedCredential;
                linkedService.certificateCommonName = Constants.maskedCredential;
                if (typeProperties.password) {
                    parameterizableSecretBaseToModel(linkedService, Db2LinkedService.PASSWORD, typeProperties.password, this.context);
                    linkedService.password = Constants.maskedCredential;
                }
            } else if ((connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKV = akvToModel(connectionString as Model.AzureKeyVaultSecretReference, this.context);
                linkedService.enableAKV = true;
            }
        }

        return linkedService;
    }
}
